import { useReactiveVar } from '@apollo/client';
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';

import { trackingKeyVar } from '@/utils/previewHelper';

import useMounted from './useMounted';

type AnalyticsContextData = {
  trackEvent: (name: string, properties?: Record<string, any>) => void;
} | null;

type AnalyticsContextProviderProps = {
  children: ReactNode;
};

const AnalyticsContext = createContext<AnalyticsContextData>(null);

export const AnalyticsProvider = ({
  children,
}: AnalyticsContextProviderProps) => {
  const mounted = useMounted();
  const [thinkificAnalytics, setThinkificAnalytics] = useState<any>(null);
  const trackingKey = useReactiveVar(trackingKeyVar);

  useEffect(() => {
    if (!mounted || !trackingKey) {
      return;
    }

    const loadAnalytics = async () => {
      const getThinkificAnalytics = (await import('@/utils/analytics')).default;

      const analytics = getThinkificAnalytics(
        process.env.NEXT_PUBLIC_MIXPANEL_TOKEN!,
        trackingKey
      );

      setThinkificAnalytics(analytics);
    };

    if (process.env.NEXT_PUBLIC_MIXPANEL_TOKEN) {
      loadAnalytics();
    } else {
      // eslint-disable-next-line no-console
      console.warn(
        'No NEXT_PUBLIC_MIXPANEL_TOKEN provided. Skipping event tracking'
      );
    }
  }, [mounted, trackingKey]);

  const trackEvent = (name: string, properties?: Record<string, any>) => {
    if (!thinkificAnalytics) {
      return;
    }

    thinkificAnalytics.track(name, properties);
  };

  return (
    <AnalyticsContext.Provider value={{ trackEvent }}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);

  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};
